import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import { AuthUserModel } from '../../../core/auth/auth-user.model';
import { Logger } from '../../../core/logging/logger.service';
import { BackendUserAuthResponse, BackendUserModel } from '../models/backend-user.model';
import { AuthUserService } from './auth-user.service';
import { FirebaseAuthService, FirebaseSignInResult } from './firebase-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authStateService = inject(AuthStateService);
  private readonly firebaseAuthService = inject(FirebaseAuthService);
  private readonly authUserService = inject(AuthUserService);
  private readonly logger = inject(Logger);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);

  public async signInWithEmailPassword(email: string, password: string): Promise<void> {
    const firebaseResult = await this.firebaseAuthService.signInWithEmailPassword(email, password);
    await this.finishSignIn(firebaseResult, 'email');
  }

  public async signInWithGoogle(): Promise<void> {
    const firebaseResult = await this.firebaseAuthService.signInWithGoogle();
    await this.finishSignIn(firebaseResult, 'google');
  }

  public async signOut(): Promise<void> {
    const signedOutUserName = this.authStateService.authUser()?.displayName ?? 'Signed In User';

    this.title.setTitle('Sign Out');
    this.ga4AnalyticsService.trackPageView({
      contentId: signedOutUserName,
      pageTitle: 'Sign Out',
    });

    try {
      await this.firebaseAuthService.signOut();
    } catch (error) {
      this.logger.warn('Firebase sign-out failed; proceeding with local session teardown.', {
        feature: 'auth',
        operation: 'sign-out-firebase',
        severity: 'medium',
        error,
      });
    }

    try {
      await firstValueFrom(this.authUserService.signOut());
    } catch (error) {
      this.logger.warn('Backend sign-out failed; proceeding with local session teardown.', {
        feature: 'auth',
        operation: 'sign-out-backend',
        severity: 'medium',
        error,
      });
    } finally {
      this.authStateService.clearAuth();
      await this.router.navigateByUrl('/signin');
    }
  }

  private async finishSignIn(
    firebaseResult: FirebaseSignInResult,
    method: 'email' | 'google',
  ): Promise<void> {
    this.authStateService.setAuthUser({
      uid: firebaseResult.uid,
      token: firebaseResult.uid,
      displayName:
        firebaseResult.displayName?.trim() || firebaseResult.email?.trim() || 'Signed In User',
      email: firebaseResult.email,
    });

    try {
      const backendAuthResponse = await firstValueFrom(
        this.authUserService.getUserByUid(firebaseResult.uid),
      );
      const authUser = this.mapToAuthUser(firebaseResult, backendAuthResponse);
      this.authStateService.setAuthUser(authUser);
    } catch (error) {
      this.logger.warn('Backend user hydration failed; continuing with Firebase user profile.', {
        feature: 'auth',
        operation: 'sign-in-backend-user-hydration',
        severity: 'medium',
        error,
      });
    }

    const signedInUserName =
      this.authStateService.authUser()?.displayName ??
      firebaseResult.displayName?.trim() ??
      firebaseResult.email?.trim() ??
      'Signed In User';

    this.ga4AnalyticsService.trackEvent('login', {
      method,
      content_id: signedInUserName,
    });
  }

  private mapToAuthUser(
    firebaseResult: FirebaseSignInResult,
    backendAuthResponse: BackendUserAuthResponse,
  ): AuthUserModel {
    const backendUser: BackendUserModel = backendAuthResponse.user ?? {};
    const backendDisplayName = backendUser.displayName?.trim() || backendUser.name?.trim();
    const firebaseDisplayName = firebaseResult.displayName?.trim();
    const fallbackName = firebaseResult.email?.trim() || 'Signed In User';
    const token =
      backendAuthResponse.token?.trim() ||
      backendUser.token?.trim() ||
      backendUser._id?.trim() ||
      firebaseResult.uid;

    return {
      uid: firebaseResult.uid,
      token,
      backendUserId: backendUser._id?.trim() || undefined,
      displayName: backendDisplayName || firebaseDisplayName || fallbackName,
      email: backendUser.email?.trim() || firebaseResult.email,
    };
  }
}
